import { ApolloError } from '@apollo/client';
import { Alert, Box, Card, Center, Skeleton, VStack } from '@chakra-ui/react';
import { useFocusedBranchContext } from '@client/page/management/table-group/table-group-list/component/branch-selection/focused-branch-context.ts';
import useReservationConfirmationStatusRate from '@client/page/management/table-reservation/analytic-new/logic/use-reservation-confirmation-status-rate.ts';
import { useTranslate } from '@tolgee/react';
import { ChartOptions, type ChartData } from 'chart.js';
import { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { BellAlertIcon } from '@heroicons/react/24/outline';

export default function ReservationGroupByConfirmationStatus(props: { startTime: Dayjs; endTime: Dayjs }) {
  const { branch } = useFocusedBranchContext();
  const { t } = useTranslate();

  const { data, loading, error } = useReservationConfirmationStatusRate(
    branch?.id,
    props.startTime.format(),
    props.endTime.format(),
  );

  const [chartData, setChartData] = useState<ChartData<'doughnut'>>({ labels: [], datasets: [] });

  useEffect(() => {
    if (!data) {
      return;
    }

    const aggregateData = data.analytic_reservationsByConfirmationStatus;

    // One day
    const labels = aggregateData.map((item) => item.status.toLowerCase());

    const everydayTrends = aggregateData.map((item) => item.count);

    setChartData({
      labels: labels,
      datasets: [
        {
          label: 'Orders',
          data: everydayTrends,
          borderWidth: 2,
          borderRadius: 0,
          backgroundColor: aggregateData.map((value) => {
            switch (value.status) {
              case 'CONFIRMED':
                return 'rgba(74,250,76,0.5)';
              case 'PENDING':
                return 'rgba(255,198,0,0.5)';
              case 'CANCELLED':
                return 'rgba(255,75,75,0.5)';
              default:
                return 'rgba(255,198,0,0.5)';
            }
          }),
        },
      ],
    });
  }, [data, setChartData]);

  if (loading) {
    return (
      <VStack gap={4} align="stretch">
        <Skeleton height="40px" />
        <Skeleton height="300px" />
      </VStack>
    );
  }

  if (error && error instanceof ApolloError) {
    return (
      <Alert.Root status="error" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Box>
          <Alert.Title>{t('Error loading confirmation status')}</Alert.Title>
          <Alert.Description>{error.message}</Alert.Description>
        </Box>
      </Alert.Root>
    );
  }

  if (data?.analytic_reservationsByConfirmationStatus.length == 0) {
    return null;
  }

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  return (
    <Card.Root>
      <Card.Header p={0}>
        <Card.Title fontWeight={'semibold'}>{t('Booking Outcome Ratio')}</Card.Title>
      </Card.Header>
      <Card.Body p={0}>
        <Center rounded={'xl'} mt={4} p={4} w="full" bgColor={'gray.50'} border={'1px solid'} borderColor={'gray.200'}>
          <Center>
            <Doughnut options={options} data={chartData} />
          </Center>
        </Center>
      </Card.Body>
    </Card.Root>
  );
}
